import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import React, { useMemo } from 'react'
import CellContent from './CellContent'
import { getHotelSearchBestPackage } from './utils'

interface Props {
  offer: App.HotelOffer
  filters: App.OfferListFilters
  packageId?: string
}

function LEHotelSearchCancellationPolicyCell({ offer, filters, packageId }: Props) {
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const rooms = filters.rooms

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, rooms), [checkIn, checkOut, rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration

  const hasFiltersSet = !!(checkIn && checkOut && rooms?.length)

  const selectedPackage = offer.packages.find(p => p.id === packageId)
  const bestPackage = useMemo<App.HotelPackage | undefined>(() => getHotelSearchBestPackage({
    bestPriceError,
    bestMaybeAvailableRate,
    hasFiltersSet,
    metaData,
    offer,
    shouldUseRealSearchPrices,
    duration,
  }), [bestPriceError, bestMaybeAvailableRate, hasFiltersSet, metaData, offer, shouldUseRealSearchPrices, duration])

  const cancellationPolicyType = selectedPackage ? selectedPackage.roomRate?.cancellationPolicy?.type : bestPackage?.roomRate?.cancellationPolicy?.type

  if (!cancellationPolicyType) {
    return null
  }

  return (
    <CellContent>
      <TileCancellationPolicy
        cancellationPolicyType={cancellationPolicyType}
        checkInDate={filters?.checkIn}
        timezoneOffset={offer.property.timezoneOffset}
        offerType={offer.type}
      />
    </CellContent>
  )
}

export default LEHotelSearchCancellationPolicyCell
